import "./index.css";
// src/StickyHeader/StickyHeader.tsx
import * as React3 from "react";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";

// src/StickyHeader/StickyFlashbar.tsx
import * as React2 from "react";
import Flashbar from "@cloudscape-design/components/flashbar";

// src/ContentGrid/ContentGrid.tsx
import * as React from "react";

// src/ContentGrid/ContentGrid.module.scss
var ContentGrid_module_default = {
  contentGrid: "ContentGrid_module_contentGrid2",
  contentArea: "ContentGrid_module_contentArea2"
};

// src/ContentGrid/ContentGrid.tsx
import { jsx } from "react/jsx-runtime";
var ContentGrid = ({ children, splitPanelOpen }) => {
  const [isNarrowViewport, setIsNarrowViewport] = React.useState(false);
  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1064px)");
    const handleChange = (e) => {
      setIsNarrowViewport(e.matches);
    };
    handleChange(mediaQuery);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);
  const useCompactLayout = splitPanelOpen || isNarrowViewport;
  return /* @__PURE__ */ jsx("div", { "data-testid": "content-grid", className: `${ContentGrid_module_default.contentGrid} ${useCompactLayout ? ContentGrid_module_default.compact : ""}`, children: /* @__PURE__ */ jsx("div", { "data-testid": "content-area", className: ContentGrid_module_default.contentArea, children }) });
};

// src/StickyHeader/StickyFlashbar.module.scss
var StickyFlashbar_module_default = {
  flashbarContainer: "StickyFlashbar_module_flashbarContainer",
  flashbarVisible: "StickyFlashbar_module_flashbarVisible",
  flashbarHidden: "StickyFlashbar_module_flashbarHidden",
  flashbarContent: "StickyFlashbar_module_flashbarContent"
};

// src/StickyHeader/StickyFlashbar.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var StickyFlashbar = ({ layout, headerRef, stickyId: _stickyId }) => {
  const flashbarRef = React2.useRef(null);
  const [isMobile, setIsMobile] = React2.useState(false);
  const [headerHeight, setHeaderHeight] = React2.useState(0);
  const [isStuck, setIsStuck] = React2.useState(false);
  React2.useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 687px)");
    const handleChange = (e) => setIsMobile(e.matches);
    handleChange(mediaQuery);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);
  React2.useEffect(() => {
    if (!headerRef.current)
      return;
    const updateHeaderHeight = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    updateHeaderHeight();
    const resizeObserver = new ResizeObserver(updateHeaderHeight);
    resizeObserver.observe(headerRef.current);
    return () => resizeObserver.disconnect();
  }, [headerRef]);
  React2.useEffect(() => {
    if (!headerRef.current)
      return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStuck(!entry.isIntersecting);
      },
      {
        threshold: [1],
        // On mobile, header sticks at top: 40px, so we need to account for that in the rootMargin
        rootMargin: isMobile ? "-41px 0px 0px 0px" : "-1px 0px 0px 0px"
      }
    );
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, [headerRef, isMobile]);
  const hasFlashMessages = layout.flashbar.items.length > 0;
  if (!hasFlashMessages) {
    return null;
  }
  const MOBILE_BREADCRUMB_HEIGHT = 40;
  const topOffset = isMobile ? headerHeight + MOBILE_BREADCRUMB_HEIGHT : headerHeight;
  const splitPanelPosition = layout.splitPanel.metadata.position ?? "side";
  const rightOffset = layout.splitPanel.shown && splitPanelPosition === "side" ? layout.splitPanel.metadata.size : 0;
  return /* @__PURE__ */ jsx2(
    "div",
    {
      ref: flashbarRef,
      className: `${StickyFlashbar_module_default.flashbarContainer} ${isStuck ? StickyFlashbar_module_default.flashbarVisible : StickyFlashbar_module_default.flashbarHidden}`,
      style: { top: `${topOffset}px`, right: `${rightOffset}px` },
      children: /* @__PURE__ */ jsx2(ContentGrid, { children: /* @__PURE__ */ jsx2("div", { className: StickyFlashbar_module_default.flashbarContent, children: /* @__PURE__ */ jsx2(
        Flashbar,
        {
          stackItems: true,
          items: layout.flashbar.items.map((item) => ({
            ...item,
            onDismiss: item.dismissible !== false ? () => layout.flashbar.removeItem(item.id) : void 0
          })),
          i18nStrings: {
            ariaLabel: "Notifications",
            errorIconAriaLabel: "Error",
            infoIconAriaLabel: "Information",
            inProgressIconAriaLabel: "In progress",
            successIconAriaLabel: "Success",
            warningIconAriaLabel: "Warning"
          }
        }
      ) }) })
    }
  );
};

// src/StickyHeader/StickyHeader.module.scss
var StickyHeader_module_default = {
  aiModeLink: "StickyHeader_module_aiModeLink",
  stickyHeader: "StickyHeader_module_stickyHeader",
  headerContent: "StickyHeader_module_headerContent",
  headerTitle: "StickyHeader_module_headerTitle",
  headerActions: "StickyHeader_module_headerActions"
};

// src/StickyHeader/StickyHeader.tsx
import { Fragment, jsx as jsx3, jsxs } from "react/jsx-runtime";
var StickyHeader = ({ title, actions, layout, navigation, stickyId }) => {
  const headerRef = React3.useRef(null);
  React3.useEffect(() => {
    return layout.registerSticky(headerRef, stickyId);
  }, [layout, stickyId]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx3("div", { ref: headerRef, className: StickyHeader_module_default.stickyHeader, children: /* @__PURE__ */ jsx3(ContentGrid, { splitPanelOpen: layout.splitPanel.shown, children: /* @__PURE__ */ jsxs("div", { className: StickyHeader_module_default.headerContent, children: [
      /* @__PURE__ */ jsxs("div", { className: StickyHeader_module_default.headerTitle, children: [
        /* @__PURE__ */ jsx3(Box, { fontSize: "body-s", children: /* @__PURE__ */ jsx3(
          Button,
          {
            variant: "inline-link",
            onClick: () => navigation.navigate("home", {}),
            iconName: "gen-ai",
            iconAlign: "left",
            className: StickyHeader_module_default.aiModeLink,
            children: "Agent mode"
          }
        ) }),
        /* @__PURE__ */ jsx3(Header, { variant: "h1", children: title })
      ] }),
      actions && /* @__PURE__ */ jsx3("div", { className: StickyHeader_module_default.headerActions, children: actions })
    ] }) }) }),
    /* @__PURE__ */ jsx3(StickyFlashbar, { layout, headerRef, stickyId })
  ] });
};
export {
  StickyFlashbar,
  StickyHeader
};
//# sourceMappingURL=index.js.map
